<script setup lang="ts">
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaNeutral, gaiaOnInk, gaiaScrim } from '../../themes/tokens';

const props = defineProps({
  /** Large display headline, e.g. "Manage your contracts seamlessly". */
  headline: {
    type: String,
    required: true,
  },
  /** Supporting copy under the headline. */
  subline: {
    type: String,
    default: undefined,
  },
  /**
   * Artwork behind the panel — slot an <img> or <video> into #media. The scrim
   * goes over it either way, so the copy keeps its contrast on any frame; on a
   * bare ink panel it is invisible.
   */
  scrim: {
    type: Boolean,
    default: true,
  },
});

const ink = gaiaNeutral.ink;
const surface = gaiaNeutral.surface;
const graphicOpacity = gaiaOnInk.graphicOpacity;
const scrimGradient = gaiaScrim.panel;
</script>

<template>
  <!-- Ink brand panel from the Login mock: #header (mark/nav), centered
       headline + subline, #footer (logo + © line). Bespoke marketing chrome
       on the shared ink/on-ink tokens. -->
  <n-flex
    vertical
    justify="space-between"
    :size="0"
    class="gaia-brand-panel"
    :style="{ padding: '40px 48px', background: ink, color: surface }"
  >
    <div v-if="$slots.media" class="media"><slot name="media" /></div>
    <div v-if="$slots.media && props.scrim" class="scrim" />
    <div class="layer"><slot name="header" /></div>
    <div class="layer" :style="{ maxWidth: '420px' }">
      <!-- `sans` because the brand headline is the one heading NOT set in the
           serif — the auth cards next to it still are, and that contrast is
           the point. -->
      <gaia-header
        :level="1"
        variant="display"
        size="hero"
        sans
        tone="onInkStrong"
        :style="{ lineHeight: 1.2, textWrap: 'pretty' }"
      >
        {{ props.headline }}
      </gaia-header>
      <gaia-text
        v-if="props.subline"
        size="prose"
        tone="onInk"
        :style="{ display: 'block', marginTop: '18px', lineHeight: 1.6, maxWidth: '360px' }"
      >
        {{ props.subline }}
      </gaia-text>
    </div>
    <n-flex align="center" justify="space-between" :size="16" :wrap="false" class="layer">
      <n-flex
        v-if="$slots.logo"
        inline
        align="center"
        :size="0"
        :wrap="false"
        :style="{ opacity: graphicOpacity }"
      >
        <slot name="logo" />
      </n-flex>
      <gaia-text v-if="$slots.footer" variant="label" size="micro" tone="onInkFaint">
        <slot name="footer" />
      </gaia-text>
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* The panel stacks three layers: artwork, a scrim over it, then the copy.
   Absolute positioning is the only way to overlap them, so the container and
   both backdrop layers stay CSS while the content rows remain n-flex. */
.gaia-brand-panel {
  position: relative;
  overflow: hidden;
}

.media,
.scrim {
  position: absolute;
  inset: 0;
}

/* The artwork fills the panel whatever its aspect ratio; :deep because the
   caller owns the element. */
.media :deep(img),
.media :deep(video) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Darkest at the bottom, where the logo and © line sit on the busiest part of
   most photographs. */
.scrim {
  background: v-bind(scrimGradient);
  z-index: 1;
}

.layer {
  position: relative;
  z-index: 2;
}
</style>
